import mixpanel from 'mixpanel-browser';

class MixpanelErrorTracker {
  constructor() {
    this.errorQueue = [];
    this.isProcessing = false;
    this.lastErrorTime = 0;
    this.errorCount = 0;
    this.maxErrorsPerMinute = 30; // Limit to 30 errors per minute
    this.batchSize = 10; // Process errors in smaller batches
    this.processingDelay = 1000; // 1 second delay between batches
  }

  trackError(eventName, errorData) {
    try {
      const now = Date.now();

      // Reset counter if more than a minute has passed
      if (now - this.lastErrorTime > 60000) {
        this.errorCount = 0;
        this.lastErrorTime = now;
      }

      // Rate limiting - don't track if we've exceeded the limit
      if (this.errorCount >= this.maxErrorsPerMinute) {
        console.warn('Mixpanel error tracking rate limit exceeded, skipping event:', eventName);
        return;
      }

      // Add error to queue instead of immediate tracking
      this.errorQueue.push({
        eventName,
        errorData: {
          ...errorData,
          timestamp: now,
          error_tracking_version: '1.0'
        }
      });

      this.errorCount++;

      // Process queue if not already processing
      if (!this.isProcessing) {
        this.processErrorQueue();
      }
    } catch (trackingError) {
      console.warn('Error in Mixpanel error tracking:', trackingError);
    }
  }

  async processErrorQueue() {
    if (this.isProcessing || this.errorQueue.length === 0) {
      return;
    }

    this.isProcessing = true;

    try {
      while (this.errorQueue.length > 0) {
        // Process errors in smaller batches
        const batch = this.errorQueue.splice(0, this.batchSize);

        // Track each error in the batch with a small delay
        for (const { eventName, errorData } of batch) {
          try {
            mixpanel.track(eventName, errorData);
            // Small delay between individual tracks to prevent mutex issues
            await this.delay(50);
          } catch (error) {
            console.warn('Failed to track individual error:', error);
          }
        }

        // Delay between batches to prevent overwhelming Mixpanel
        if (this.errorQueue.length > 0) {
          await this.delay(this.processingDelay);
        }
      }
    } catch (error) {
      console.warn('Error processing error queue:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // Method to track critical errors immediately (bypassing queue)
  trackCriticalError(eventName, errorData) {
    try {
      mixpanel.track(eventName, {
        ...errorData,
        critical: true,
        timestamp: Date.now()
      });
    } catch (error) {
      console.warn('Failed to track critical error:', error);
    }
  }

  // Get queue status for debugging
  getQueueStatus() {
    return {
      queueLength: this.errorQueue.length,
      isProcessing: this.isProcessing,
      errorCount: this.errorCount,
      lastErrorTime: this.lastErrorTime
    };
  }
}

// Create singleton instance
const mixpanelErrorTracker = new MixpanelErrorTracker();

export default mixpanelErrorTracker;
